// Motion helpers: the reduced motion switch, count up, the bloom fly-in and the ring burst.
// DOM only. No API calls. Pure choices live in liveliness.ts.
declare global {
  interface Window { __whhTrace?: string[]; __whhMotion?: (m: 'reduce' | 'auto') => void; __whhDay?: string; __whhHour?: number }
}

/** A short log of motion moments, read by the browser verification script. Never shown. */
export function trace(ev: string) { const w = window; (w.__whhTrace ??= []).push(ev); if (w.__whhTrace.length > 50) w.__whhTrace.shift(); }
export const reduced = () => document.documentElement.classList.contains('rm');

/** html.rm follows prefers-reduced-motion. window.__whhMotion('reduce' | 'auto') forces it for testing. */
export function watchMotion() {
  let forced: 'reduce' | null = null;
  const mq = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  const apply = () => document.documentElement.classList.toggle('rm', forced ? true : !!mq?.matches);
  mq?.addEventListener?.('change', apply);
  window.__whhMotion = (m) => { forced = m === 'reduce' ? 'reduce' : null; apply(); };
  apply();
}

/** Counts a number up in place. Under reduced motion it jumps to the end. */
export function countUp(el: Element | null, from: number, to: number, ms = 800) {
  if (!el) return;
  if (reduced() || from === to) { el.textContent = String(to); return; }
  const t0 = performance.now();
  const step = (t: number) => {
    const p = Math.min(1, (t - t0) / ms); const e = 1 - Math.pow(1 - p, 3);
    el.textContent = String(Math.round(from + (to - from) * e));
    if (p < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

export function bump(el: Element | null) { if (!el) return; el.classList.remove('bump'); void (el as HTMLElement).offsetWidth; el.classList.add('bump'); }

/** Moments waiting for the next screen. afterAction fills it; Home, the review and the challenge read it once. */
export const pending = { blooms: 0, ring: false, streak: 0, plan: false, chDay: false, at: 0 };
export const fresh = () => Date.now() - pending.at < 20000;

/** "+N" arcs from `from` (or the middle of the screen) into the header bloom chip, which bumps and counts up. */
export function flyBlooms(n: number, from?: Element | null) {
  if (!(n > 0)) return;
  pending.blooms = 0;
  const chip = document.querySelector('[data-bloomchip]');
  const b = chip?.querySelector('b') ?? null;
  const start = b ? Number(b.textContent) || 0 : 0;
  const fly = document.createElement('span');
  fly.className = 'bloomfly'; fly.setAttribute('aria-hidden', 'true'); fly.textContent = `+${n}`;
  const s = from?.getBoundingClientRect() ?? { left: window.innerWidth / 2 - 20, top: window.innerHeight * 0.55, width: 40, height: 24 };
  fly.style.left = `${s.left + s.width / 2}px`; fly.style.top = `${s.top + s.height / 2}px`;
  document.body.appendChild(fly);
  const land = () => { fly.remove(); if (chip) { bump(chip); countUp(b, start, start + n, 500); } };
  if (reduced() || !chip) { trace(`fly:${n}:fade`); fly.classList.add('fade'); setTimeout(land, 900); return; }
  trace(`fly:${n}:arc`);
  const d = chip.getBoundingClientRect();
  fly.style.setProperty('--dx', `${d.left + d.width / 2 - (s.left + s.width / 2)}px`);
  fly.style.setProperty('--dy', `${d.top + d.height / 2 - (s.top + s.height / 2)}px`);
  fly.classList.add('arc');
  setTimeout(land, 720);
}

const SPARK_COLORS = ['#E3A857', '#A94B3F', '#5F7F66', '#F6D59A', '#D9A98F'];
/** The twelve sparks every celebration shares. */
export const sparksHtml = (n = 12) => Array.from({ length: n }, (_, i) => `<span class="spark" style="transform:rotate(${i * (360 / n)}deg)"><i style="background:${SPARK_COLORS[i % 5]};animation-delay:${i * 0.02}s"></i></span>`).join('');

/** Home: the ring just closed. Sparks and one soft ring out over the arcs, 1.4 s, never blocking. */
export function ringBurst() {
  const card = document.querySelector('.ringcard'); const wrap = card?.querySelector('.ringwrap3');
  if (!card || !wrap) return;
  trace('ring');
  pending.ring = false;
  card.classList.add('celebrating');
  const c = document.createElement('div'); c.className = 'celebrate'; c.setAttribute('aria-hidden', 'true');
  c.innerHTML = `<div class="rip"></div>${sparksHtml()}`;
  wrap.appendChild(c);
  setTimeout(() => { c.remove(); card.classList.remove('celebrating'); }, 1400);
}
